import { useEffect, useRef } from 'react';
import { useSocketStore } from '@/stores/socket';

/**
 * Noms des events WebSocket "mutations de domaine" émis par le backend.
 *
 * Pattern sécurité (cf SECURITY.md §11) :
 *  - payload minimal `{ id, actorId, ... }` - aucune donnée métier exposée,
 *  - rooms server-controlled `user:{userId}` (pas de join client),
 *  - les composants qui écoutent font un REFETCH via API authentifiée
 *    (re-jouant RBAC + scope filter côté serveur).
 */
export const REALTIME_EVENTS = {
  OPPORTUNITY_UPDATED: 'opportunity.updated',
  PROSPECT_UPDATED: 'prospect.updated',
  TASK_MUTATED: 'task.mutated',
} as const;

export interface OpportunityUpdatedEvent {
  id: string;
  actorId: string;
}

export interface ProspectUpdatedEvent {
  id: string;
  actorId: string;
  change: 'created' | 'updated' | 'deleted' | 'restored';
}

export interface TaskMutatedEvent {
  id: string;
  actorId: string;
  change: 'created' | 'updated' | 'completed' | 'uncompleted' | 'deleted';
}

/**
 * Abonnement déclaratif à un event WebSocket "mutation de domaine".
 *
 * ## Exemple
 * ```ts
 * useRealtimeEvent<OpportunityUpdatedEvent>('opportunity.updated', (ev) => {
 *   if (ev.id === opportunityId) void refetchScore();
 * });
 * ```
 *
 * ## Garanties
 *  - Enable/disable au gré des deps : si `socket` n'est pas connecté, le hook
 *    ne fait rien (no-op sûr au bootstrap).
 *  - Auto-unsubscribe au unmount ou au changement du socket / callback.
 *  - Le callback est capturé dans un closure stable par le store ; on ré-abonne
 *    à chaque changement de `cb` pour toujours pointer sur la version fraîche
 *    des props/state.
 */
export function useRealtimeEvent<T = unknown>(event: string, cb: (payload: T) => void): void {
  const socket = useSocketStore((s) => s.socket);
  const onEvent = useSocketStore((s) => s.onEvent);

  useEffect(() => {
    if (!socket) return;
    return onEvent<T>(event, cb);
  }, [socket, onEvent, event, cb]);
}

/**
 * Variante debouncée : écoute N events WebSocket simultanés et appelle `cb`
 * une seule fois après `delayMs` de calme (aucun event supplémentaire reçu).
 *
 * Usage typique : Dashboard / KPI Direction qui ont des queries lourdes
 * (agrégations) et ne doivent pas refetch à chaque mutation individuelle en
 * cas d'import en masse (100+ opportunités créées en 10s).
 *
 * Le callback ne reçoit aucun payload — le cas d'usage est un simple refetch
 * global de la page. Pour un filtrage fin par id, utiliser `useRealtimeEvent`
 * directement.
 *
 * ```ts
 * useDebouncedRealtimeRefresh(
 *   [REALTIME_EVENTS.OPPORTUNITY_UPDATED, REALTIME_EVENTS.PROSPECT_UPDATED],
 *   () => void loadDashboard(),
 *   2000,
 * );
 * ```
 */
export function useDebouncedRealtimeRefresh(
  events: readonly string[],
  refresh: () => void,
  delayMs = 2000,
): void {
  const socket = useSocketStore((s) => s.socket);
  const onEvent = useSocketStore((s) => s.onEvent);
  // On garde refresh dans une ref pour que le useEffect ne se resubscribe
  // pas à chaque render (le callback peut être une fonction inline non
  // memoized par le consommateur).
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!socket) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = (): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        refreshRef.current();
        timer = null;
      }, delayMs);
    };
    const unsubscribers = events.map((ev) => onEvent(ev, schedule));
    return () => {
      if (timer) clearTimeout(timer);
      for (const u of unsubscribers) u();
    };
  }, [socket, onEvent, events, delayMs]);
}
